// Streams the AI-assisted report narrative from the dedicated reporting SSE
// endpoint and splits it into the tagged blocks the page routes to each panel.
// The blocks are defined by BLOCK_KEYS in data_api/api/reporting_ai.py
// (executive_summary, cdi_note, cdi_trend_note, spi_trend_note, veg_soil_note,
// outlook_note).
//
// The narrative arrives as `[[block_key]]` markers followed by prose. We
// re-parse the whole accumulated string on every token so panels fill in
// progressively as the stream advances.

import { useEffect, useMemo, useState } from "react";

import { base_url } from "../Geoportal";

const ENDPOINT = `${base_url}/api/data_api/reporting/ai-summary/`;
const BLOCK_RE = /\[\[(\w+)\]\]/g;

/**
 * Split `[[key]] text [[key2]] text2` into `{ key: "text", key2: "text2" }`.
 * @param {string} text
 * @returns {Record<string, string>}
 */
function parseBlocks(text) {
  if (!text) return {};
  const blocks = {};
  const matches = [...text.matchAll(BLOCK_RE)];
  if (matches.length === 0) {
    // No marker yet — treat everything so far as the executive summary.
    return { executive_summary: text.trim() };
  }
  matches.forEach((match, i) => {
    const key = match[1];
    const start = match.index + match[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    blocks[key] = text.slice(start, end).trim();
  });
  return blocks;
}

/**
 * @param {object} params
 * @param {string|number} params.countryId
 * @param {string} params.countryName
 * @param {string|number} params.year
 * @param {string} params.monthInt Month as a zero-padded string.
 * @param {string} params.dekad
 * @param {"national"|"regional"} params.scope
 * @param {string|null} params.adminOne
 * @param {boolean} params.enabled
 * @returns {{blocks: Record<string, string>, streaming: boolean, error: string|null}}
 */
export default function useReportAiSummary({
  countryId,
  countryName,
  year,
  monthInt,
  dekad,
  scope,
  adminOne,
  enabled,
}) {
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !countryId || !year || !monthInt || !dekad) return undefined;

    const qs = new URLSearchParams({
      country_id: String(countryId),
      country_name: countryName || "",
      year: String(year),
      month: String(Number(monthInt)),
      dekad: String(dekad),
      scope,
    });
    if (scope === "regional" && adminOne) qs.set("admin_one", adminOne);

    setText("");
    setError(null);
    setStreaming(true);

    const source = new EventSource(`${ENDPOINT}?${qs.toString()}`);
    let acc = "";

    source.addEventListener("token", (event) => {
      try {
        const { text: chunk } = JSON.parse(event.data);
        if (chunk) {
          acc += chunk;
          setText(acc);
        }
      } catch (e) {
        /* ignore malformed frame */
      }
    });

    source.addEventListener("done", () => {
      setStreaming(false);
      source.close();
    });

    source.addEventListener("error", () => {
      // EventSource fires a generic error on network drop and on stream end;
      // only surface it if we never received anything.
      setStreaming(false);
      if (!acc) setError("AI summary unavailable.");
      source.close();
    });

    return () => {
      source.close();
      setStreaming(false);
    };
  }, [enabled, countryId, countryName, year, monthInt, dekad, scope, adminOne]);

  const blocks = useMemo(() => parseBlocks(text), [text]);

  return { blocks, streaming, error };
}
